import api from "./api";

const biometricDeviceService = {
  // ============================================
  // DEVICE MANAGEMENT
  // ============================================

  /**
   * Get all biometric devices with optional filters
   * @param {Object} filters - Filter parameters (status, vendor, location, etc.)
   * @param {number} page - Page number for pagination
   * @param {number} limit - Items per page
   * @returns {Promise} Response with devices list
   */
  getDevices: async (filters = {}, page = 1, limit = 10) => {
    const queryParams = new URLSearchParams();

    // Add pagination
    queryParams.append("page", page);
    queryParams.append("limit", limit);

    // Add filters if provided
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        queryParams.append(key, filters[key]);
      }
    });

    const response = await api.get(
      `/api/attendance/devices?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * Get single device by ID
   * @param {string} id - Device ID
   * @returns {Promise} Response with device details
   */
  getDeviceById: async (id) => {
    const response = await api.get(`/api/attendance/devices/${id}`);
    return response.data;
  },

  /**
   * Create a new biometric device
   * @param {Object} deviceData - Device information
   * @returns {Promise} Response with created device
   */
  createDevice: async (deviceData) => {
    const response = await api.post("/api/attendance/devices", deviceData);
    return response.data;
  },

  /**
   * Update existing device
   * @param {string} id - Device ID
   * @param {Object} deviceData - Updated device information
   * @returns {Promise} Response with updated device
   */
  updateDevice: async (id, deviceData) => {
    const response = await api.put(`/api/attendance/devices/${id}`, deviceData);
    return response.data;
  },

  /**
   * Delete device (soft delete)
   * @param {string} id - Device ID
   * @returns {Promise} Response with deletion confirmation
   */
  deleteDevice: async (id) => {
    const response = await api.delete(`/api/attendance/devices/${id}`);
    return response.data;
  },

  // ============================================
  // DEVICE SYNC & HEALTH
  // ============================================

  /**
   * Manually trigger sync for a device
   * @param {string} id - Device ID
   * @returns {Promise} Response with sync result
   */
  syncDevice: async (id) => {
    const response = await api.post(`/api/attendance/devices/${id}/sync`);
    return response.data;
  },

  /**
   * Get device health status
   * @param {string} id - Device ID
   * @returns {Promise} Response with health status
   */
  getDeviceHealth: async (id) => {
    const response = await api.get(`/api/attendance/devices/${id}/health`);
    return response.data;
  },

  /**
   * Get device statistics
   * @param {string} id - Device ID
   * @returns {Promise} Response with device stats
   */
  getDeviceStats: async (id) => {
    const response = await api.get(`/api/attendance/devices/${id}/stats`);
    return response.data;
  },

  /**
   * Get processing stats for all devices
   * @returns {Promise} Response with processing statistics
   */
  getProcessingStats: async () => {
    const response = await api.get("/api/attendance/devices/processing-stats");
    return response.data;
  },

  // ============================================
  // DEVICE-USER MAPPING
  // ============================================

  /**
   * Get all device-user mappings
   * @param {Object} filters - Filter parameters
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise} Response with mappings list
   */
  getDeviceMappings: async (filters = {}, page = 1, limit = 10) => {
    const queryParams = new URLSearchParams();

    queryParams.append("page", page);
    queryParams.append("limit", limit);

    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        queryParams.append(key, filters[key]);
      }
    });

    const response = await api.get(
      `/api/attendance/device-mappings?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * Get mappings for specific device
   * @param {string} deviceId - Device ID
   * @returns {Promise} Response with device mappings
   */
  getDeviceMappingsByDevice: async (deviceId) => {
    const response = await api.get(
      `/api/attendance/device-mappings?deviceId=${deviceId}`,
    );
    return response.data;
  },

  /**
   * Get mappings for specific employee
   * @param {string} employeeId - Employee ID
   * @returns {Promise} Response with employee mappings
   */
  getMappingsByEmployee: async (employeeId) => {
    const response = await api.get(
      `/api/attendance/device-mappings/employee/${employeeId}`,
    );
    return response.data;
  },

  /**
   * Get unverified mappings
   * @returns {Promise} Response with unverified mappings
   */
  getUnverifiedMappings: async () => {
    const response = await api.get(
      "/api/attendance/device-mappings/unverified",
    );
    return response.data;
  },

  /**
   * Create a new device-user mapping
   * @param {Object} mappingData - Mapping information
   * @returns {Promise} Response with created mapping
   */
  createMapping: async (mappingData) => {
    const response = await api.post(
      "/api/attendance/device-mappings",
      mappingData,
    );
    return response.data;
  },

  /**
   * Create multiple mappings in bulk
   * @param {Array} mappings - Array of mapping objects
   * @returns {Promise} Response with bulk creation result
   */
  createBulkMappings: async (mappings) => {
    const response = await api.post("/api/attendance/device-mappings/bulk", {
      mappings,
    });
    return response.data;
  },

  /**
   * Update existing mapping
   * @param {string} id - Mapping ID
   * @param {Object} mappingData - Updated mapping information
   * @returns {Promise} Response with updated mapping
   */
  updateMapping: async (id, mappingData) => {
    const response = await api.put(
      `/api/attendance/device-mappings/${id}`,
      mappingData,
    );
    return response.data;
  },

  /**
   * Delete mapping
   * @param {string} id - Mapping ID
   * @returns {Promise} Response with deletion confirmation
   */
  deleteMapping: async (id) => {
    const response = await api.delete(`/api/attendance/device-mappings/${id}`);
    return response.data;
  },

  /**
   * Verify a mapping
   * @param {string} id - Mapping ID
   * @returns {Promise} Response with verified mapping
   */
  verifyMapping: async (id) => {
    const response = await api.post(
      `/api/attendance/device-mappings/${id}/verify`,
    );
    return response.data;
  },

  /**
   * Activate a mapping
   * @param {string} id - Mapping ID
   * @returns {Promise} Response with activated mapping
   */
  activateMapping: async (id) => {
    const response = await api.post(
      `/api/attendance/device-mappings/${id}/activate`,
    );
    return response.data;
  },

  /**
   * Deactivate a mapping
   * @param {string} id - Mapping ID
   * @returns {Promise} Response with deactivated mapping
   */
  deactivateMapping: async (id) => {
    const response = await api.post(
      `/api/attendance/device-mappings/${id}/deactivate`,
    );
    return response.data;
  },

  // ============================================
  // LOG PROCESSING
  // ============================================

  /**
   * Get unmapped logs
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Response with unmapped logs
   */
  getUnmappedLogs: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        queryParams.append(key, filters[key]);
      }
    });

    const response = await api.get(
      `/api/attendance/devices/unmapped-logs?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * Process logs manually
   * @param {Object} options - Processing options
   * @returns {Promise} Response with processing result
   */
  processLogs: async (options = {}) => {
    const response = await api.post(
      "/api/attendance/devices/process-logs",
      options,
    );
    return response.data;
  },

  /**
   * Detect duplicate logs
   * @param {Object} options - Detection options
   * @returns {Promise} Response with duplicate detection result
   */
  detectDuplicates: async (options = {}) => {
    const response = await api.post(
      "/api/attendance/devices/detect-duplicates",
      options,
    );
    return response.data;
  },

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Get available vendors list
   * @returns {Array} List of supported vendors
   */
  getVendors: () => {
    return [
      { value: "ZKTeco", label: "ZKTeco" },
      { value: "Hikvision", label: "Hikvision" },
      { value: "Honeywell", label: "Honeywell" },
      { value: "Suprema", label: "Suprema" },
      { value: "eSSL", label: "eSSL" },
      { value: "Realtime", label: "Realtime" },
      { value: "Other", label: "Other" },
    ];
  },

  /**
   * Get device status options
   * @returns {Array} List of device statuses
   */
  getDeviceStatuses: () => {
    return [
      { value: "ACTIVE", label: "Active", colorScheme: "green" },
      { value: "INACTIVE", label: "Inactive", colorScheme: "gray" },
      { value: "MAINTENANCE", label: "Maintenance", colorScheme: "orange" },
      { value: "DISABLED", label: "Disabled", colorScheme: "red" },
    ];
  },

  /**
   * Get sync mode options
   * @returns {Array} List of sync modes
   */
  getSyncModes: () => {
    return [
      { value: "PULL", label: "Pull (System Fetches)", colorScheme: "blue" },
      { value: "PUSH", label: "Push (Device Sends)", colorScheme: "purple" },
    ];
  },

  /**
   * Get mapping status options
   * @returns {Array} List of mapping statuses
   */
  getMappingStatuses: () => {
    return [
      { value: "VERIFIED", label: "Verified", colorScheme: "green" },
      { value: "PENDING", label: "Pending", colorScheme: "yellow" },
      { value: "ACTIVE", label: "Active", colorScheme: "blue" },
      { value: "INACTIVE", label: "Inactive", colorScheme: "gray" },
    ];
  },

  /**
   * Get timezone options (Indian timezones)
   * @returns {Array} List of timezones
   */
  getTimezones: () => {
    return [
      { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
      { value: "Asia/Calcutta", label: "Asia/Calcutta (IST)" },
    ];
  },
};

export default biometricDeviceService;
